import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, Leaf, Fish, Flame, Apple, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { DietaryPreferencesData } from "./DietaryPreferences";

interface DietaryRestrictionsEditorProps {
  initialPreferences: DietaryPreferencesData;
  onSave: (preferences: DietaryPreferencesData) => void;
  onClose: () => void;
}

const lifestyleOptions = [
  { id: "vegan", label: "Vegan", icon: Leaf },
  { id: "vegetarian", label: "Vegetarian", icon: Leaf },
  { id: "pescetarian", label: "Pescetarian", icon: Fish },
  { id: "keto", label: "Keto", icon: Flame },
  { id: "paleo", label: "Paleo", icon: Apple },
];

const allergenOptions = [
  { id: "gluten-free", label: "Gluten-Free" },
  { id: "dairy-free", label: "Dairy-Free" },
  { id: "nut-free", label: "Nut-Free" },
  { id: "egg-free", label: "Egg-Free" },
  { id: "soy-free", label: "Soy-Free" },
  { id: "wheat-free", label: "Wheat-Free" },
  { id: "fish-free", label: "Fish-Free" },
  { id: "shellfish-free", label: "Shellfish-Free" },
  { id: "sesame-free", label: "Sesame-Free" },
];

export const DietaryRestrictionsEditor = ({ 
  initialPreferences, 
  onSave, 
  onClose 
}: DietaryRestrictionsEditorProps) => {
  const [selectedLifestyles, setSelectedLifestyles] = useState<string[]>(initialPreferences.lifestyles);
  const [selectedAllergens, setSelectedAllergens] = useState<string[]>(initialPreferences.allergens);

  const toggleLifestyle = (id: string) => {
    setSelectedLifestyles((prev) => {
      if (prev.includes(id)) {
        return prev.filter((l) => l !== id);
      }
      if (prev.length >= 1) {
        toast({
          title: "Limit reached",
          description: "You can only select one lifestyle.",
          duration: 3000,
        });
        return prev;
      }
      return [...prev, id];
    });
  };

  const toggleAllergen = (id: string) => {
    setSelectedAllergens((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const handleSave = () => {
    onSave({
      lifestyles: selectedLifestyles,
      allergens: selectedAllergens,
    });
    onClose();
  };

  return (
    <div className="flex flex-col h-full max-h-[70vh] overflow-hidden">
      <div className="flex-1 overflow-y-auto px-1 pb-4">
        {/* Section 1: Lifestyles */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
            Lifestyle (select one)
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {lifestyleOptions.map((option) => {
              const isSelected = selectedLifestyles.includes(option.id);
              const Icon = option.icon;
              return (
                <button
                  key={option.id}
                  onClick={() => toggleLifestyle(option.id)}
                  className={`relative p-3 rounded-xl border-2 text-left transition-all ${
                    isSelected
                      ? "border-primary bg-secondary"
                      : "border-border bg-card hover:border-muted-foreground/30"
                  }`}
                >
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-2 right-2 w-4 h-4 rounded-full gradient-primary flex items-center justify-center"
                    >
                      <Check className="w-2.5 h-2.5 text-primary-foreground" />
                    </motion.div>
                  )}
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                    <span className="text-sm font-medium text-foreground">{option.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Section 2: Allergens */}
        <div>
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Allergens & Sensitivities
          </h3>
          <div className="grid grid-cols-3 gap-2">
            {allergenOptions.map((option) => {
              const isSelected = selectedAllergens.includes(option.id);
              return (
                <button
                  key={option.id}
                  onClick={() => toggleAllergen(option.id)}
                  className={`relative p-2 rounded-lg border-2 text-center transition-all ${
                    isSelected
                      ? "border-primary bg-secondary"
                      : "border-border bg-card hover:border-muted-foreground/30"
                  }`}
                >
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-1 right-1 w-3 h-3 rounded-full gradient-primary flex items-center justify-center"
                    >
                      <Check className="w-2 h-2 text-primary-foreground" />
                    </motion.div>
                  )}
                  <span className="text-xs font-medium text-foreground">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-border">
        <Button 
          onClick={handleSave} 
          className="w-full h-12 gradient-primary hover:opacity-90 transition-opacity"
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};
